import { Controller } from 'stimulus'
import languages from '../data/languages.json'

// Searchable multi-select that renders chips and hidden inputs for Rails params
export default class extends Controller {
  connect() {
    // Prevent double-initialisation
    if (this._initialized) return
    this._initialized = true

    // 1) Explicit param name wins
    const explicitParam = this.data.get('searchable-multi-select-param-name')
    // 2) Then form-type mapping
    const formType = this.data.get('form-type')
    // 3) Finally, URL fallback
    const isStudentForm = window.location.pathname.includes('student_surveys')
    const isStaffForm = window.location.pathname.includes('staff_surveys')

    if (explicitParam) {
      this.paramName = explicitParam
    } else if (formType === 'student') {
      this.paramName = 'student_survey[languages_spoken][]'
    } else if (formType === 'staff') {
      this.paramName = 'staff_survey[languages_spoken][]'
    } else if (isStudentForm) {
      this.paramName = 'student_survey[languages_spoken][]'
    } else if (isStaffForm) {
      this.paramName = 'staff_survey[languages_spoken][]'
    } else {
      this.paramName = 'staff_survey[languages_spoken][]'
    }

    // Prepare data
    const rawPreselected = this._parseSelected(this.data.get('searchable-multi-select-selected'))
    // Validate against known languages and dedupe
    this.preselected = Array.from(new Set((rawPreselected || []).filter((v) => languages.includes(v))))
    this.selectedValues = new Set(this.preselected)

    // A11y state
    this.activeIndex = -1
    this.dropdownId = `smc-${Math.random().toString(36).slice(2)}`

    this._build()
  }

  _parseSelected(selectedJson) {
    try {
      const parsed = JSON.parse(selectedJson || '[]')
      return Array.isArray(parsed) ? parsed : []
    } catch (_e) {
      return []
    }
  }

  _build() {
    // Wrapper
    const wrapper = document.createElement('div')
    wrapper.className = 'searchable-select-wrapper'

    // Chips container
    const chips = document.createElement('div')
    chips.className = 'searchable-multi-chips mb-2 flex flex-wrap gap-2'

    // Search input
    const input = document.createElement('input')
    input.type = 'text'
    input.className = 'searchable-select-input'
    input.placeholder = 'Search languages...'
    // A11y attributes
    input.setAttribute('role', 'combobox')
    input.setAttribute('aria-autocomplete', 'list')
    input.setAttribute('aria-expanded', 'false')
    input.addEventListener('input', (e) => this._filter(e.target.value))
    input.addEventListener('focus', () => this._show())
    input.addEventListener('blur', () => setTimeout(() => this._hide(), 200))
    input.addEventListener('keydown', (e) => this._onKeydown(e))

    // Dropdown
    const dropdown = document.createElement('div')
    dropdown.className = 'searchable-select-dropdown max-h-64 overflow-auto'
    dropdown.style.display = 'none'
    dropdown.setAttribute('role', 'listbox')
    dropdown.id = this.dropdownId
    input.setAttribute('aria-controls', this.dropdownId)

    // Options
    this.optionEls = languages.map((label) => {
      const el = document.createElement('div')
      el.className = 'searchable-select-option'
      el.textContent = label
      el.dataset.value = label
      el.addEventListener('click', () => this._toggle(label))
      el.setAttribute('role', 'option')
      el.id = `${this.dropdownId}-${label.replace(/\s+/g, '-')}`
      dropdown.appendChild(el)
      return el
    })

    // Empty-state element
    const empty = document.createElement('div')
    empty.className = 'searchable-select-empty p-2 text-sm text-gray-600'
    empty.textContent = 'No results'
    empty.style.display = 'none'
    dropdown.appendChild(empty)

    // Hidden inputs container
    const hidden = document.createElement('div')

    // Compose
    wrapper.appendChild(chips)
    wrapper.appendChild(input)
    wrapper.appendChild(dropdown)
    wrapper.appendChild(hidden)
    this.element.appendChild(wrapper)

    // Store refs
    this.inputEl = input
    this.dropdownEl = dropdown
    this.chipsEl = chips
    this.hiddenEl = hidden
    this.emptyEl = empty

    // Seed any preselected values
    this.preselected.forEach((v) => this._select(v, { silent: true }))
    this._syncUI()
  }

  _filter(term) {
    const t = (term || '').toLowerCase()
    let visibleCount = 0
    this.optionEls.forEach((el) => {
      const isVisible = el.textContent.toLowerCase().includes(t)
      el.style.display = isVisible ? 'block' : 'none'
      if (isVisible) visibleCount += 1
    })
    this.emptyEl.style.display = visibleCount === 0 ? 'block' : 'none'
    // Reset active index when filtering
    this.activeIndex = -1
    this.inputEl.removeAttribute('aria-activedescendant')
  }

  _show() {
    this.dropdownEl.style.display = 'block'
    this.inputEl.setAttribute('aria-expanded', 'true')
  }

  _hide() {
    this.dropdownEl.style.display = 'none'
    this.inputEl.setAttribute('aria-expanded', 'false')
    this.activeIndex = -1
    this.inputEl.removeAttribute('aria-activedescendant')
  }

  _toggle(value) {
    if (this.selectedValues.has(value)) {
      this._deselect(value)
    } else {
      this._select(value)
    }
    this._syncUI()
  }

  _select(value, { silent = false } = {}) {
    if (!languages.includes(value)) return
    this.selectedValues.add(value)
    if (!silent) this.inputEl.value = ''
  }

  _deselect(value) {
    this.selectedValues.delete(value)
  }

  _syncUI() {
    // Chips
    this.chipsEl.innerHTML = ''
    Array.from(this.selectedValues).forEach((value) => {
      const chip = document.createElement('span')
      chip.className = 'inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs md:text-sm'
      chip.textContent = value
      const x = document.createElement('button')
      x.type = 'button'
      x.className = 'ml-2 text-green-700 hover:text-green-900'
      x.textContent = '×'
      x.addEventListener('click', () => {
        this._deselect(value)
        this._syncUI()
      })
      chip.appendChild(x)
      this.chipsEl.appendChild(chip)
    })

    // Hidden inputs
    this.hiddenEl.innerHTML = ''
    Array.from(this.selectedValues).forEach((value) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = this.paramName
      input.value = value
      this.hiddenEl.appendChild(input)
    })

    // Update option aria-selected
    const selected = new Set(this.selectedValues)
    this.optionEls.forEach((el) => {
      el.setAttribute('aria-selected', selected.has(el.dataset.value) ? 'true' : 'false')
    })
  }

  _visibleOptions() {
    return this.optionEls.filter((el) => el.style.display !== 'none')
  }

  _moveActive(delta) {
    const visible = this._visibleOptions()
    if (visible.length === 0) return
    this.activeIndex = (this.activeIndex + delta + visible.length) % visible.length
    const activeEl = visible[this.activeIndex]
    this.inputEl.setAttribute('aria-activedescendant', activeEl.id)
    // Ensure active option is in view
    const containerRect = this.dropdownEl.getBoundingClientRect()
    const elRect = activeEl.getBoundingClientRect()
    if (elRect.top < containerRect.top) {
      this.dropdownEl.scrollTop -= (containerRect.top - elRect.top)
    } else if (elRect.bottom > containerRect.bottom) {
      this.dropdownEl.scrollTop += (elRect.bottom - containerRect.bottom)
    }
  }

  _onKeydown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this._show()
      this._moveActive(1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      this._show()
      this._moveActive(-1)
    } else if (e.key === 'Enter') {
      const visible = this._visibleOptions()
      if (this.activeIndex >= 0 && this.activeIndex < visible.length) {
        e.preventDefault()
        const value = visible[this.activeIndex].dataset.value
        this._toggle(value)
      }
    } else if (e.key === 'Escape') {
      this._hide()
    }
  }
}


